import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiSave,
    FiX,
    FiSearch,
    FiUser,
    FiShield,
    FiBriefcase,
    FiUsers,
    FiBook,
    FiDollarSign,
    FiHome,
    FiFileText,
    FiEye,
    FiEyeOff,
} from "react-icons/fi";
import userService from "../../services/userService";
import api from "../../services/api";
import { useApp } from "../../context/AppContext";

const ROLES = [
    "admin",
    "teacher",
    "librarian",
    "accountant",
    "warden",
    "clerk",
    "student",
    "parent",
    "storekeeper"
];

const STAFF_ROLES = ["teacher", "librarian", "accountant", "warden", "clerk", "storekeeper"];

const ROLE_CONFIG = {
    admin: {
        color: "#dc2626",
        bg: "#fee2e2",
        icon: FiShield,
        label: "Admin",
        hint: "Full access to college administration modules.",
    },
    teacher: {
        color: "#7c3aed",
        bg: "#ede9fe",
        icon: FiBriefcase,
        label: "Teacher",
        hint: "Access to assigned subjects, attendance, results, and homework.",
    },
    librarian: {
        color: "#0284c7",
        bg: "#e0f2fe",
        icon: FiBook,
        label: "Librarian",
        hint: "Access to book catalog, issue/returns, and library fines.",
    },
    accountant: {
        color: "#059669",
        bg: "#d1fae5",
        icon: FiDollarSign,
        label: "Accountant",
        hint: "Access to student fees, receipts, payroll, and financial reports.",
    },
    warden: {
        color: "#d97706",
        bg: "#fef3c7",
        icon: FiHome,
        label: "Warden",
        hint: "Access to hostel room allocations, hostel fees, and student tracking.",
    },
    clerk: {
        color: "#4b5563",
        bg: "#f3f4f6",
        icon: FiFileText,
        label: "Clerk",
        hint: "Access to student certificates, bonafide/leaving, and inquiries.",
    },
    student: {
        color: "#003b67",
        bg: "#e8f1f8",
        icon: FiUser,
        label: "Student",
        hint: "Portal access to personal academic records, timetable, and fees.",
    },
    parent: {
        color: "#0d9488",
        bg: "#ccfbf1",
        icon: FiUsers,
        label: "Parent",
        hint: "Portal access to linked child's attendance, fees, and results.",
    },
    storekeeper: {
        color: "#0d9488",
        bg: "#ccfbf1",
        icon: FiUsers,
        label: "Storekeeper",
        hint: "Portal access to storekeeper's inventory.",
    },
};

const EMPTY_FORM = {
    username: "",
    password: "",
    displayName: "",
    role: "student",
    linkedEmployee: "",
    linkedStudent: "",
    college: "",
    isActive: true,
};

function LinkedRecordPicker({ type, value, displayValue, onSelect, onClear }) {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const isEmployee = type === "employee";
    const label = isEmployee ? "Link to Employee Record" : "Link to Student Record";
    const placeholder = isEmployee
        ? "Search by name or employee code…"
        : "Search by name or enrollment number…";

    const search = useCallback(
        async (q) => {
            if (q.length < 2) {
                setResults([]);
                return;
            }
            setLoading(true);
            try {
                const endpoint = isEmployee ? "/employees" : "/students";
                const res = await api.get(endpoint, { params: { search: q, limit: 8 } });
                setResults(res.data.data || res.data);
            } catch {
                setResults([]);
            } finally {
                setLoading(false);
            }
        },
        [isEmployee],
    );

    useEffect(() => {
        const timer = setTimeout(() => search(query), 280);
        return () => clearTimeout(timer);
    }, [query, search]);

    if (value) {
        return (
            <div className="form-group">
                <label className="form-label">{label}</label>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        background: "#f0fdf4",
                        border: "1px solid #86efac",
                        borderRadius: "var(--radius)",
                        padding: "0.55rem 0.85rem",
                    }}
                >
                    <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>
                        {displayValue}
                    </span>
                    <button
                        type="button"
                        onClick={onClear}
                        style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "#dc2626",
                            padding: 2,
                        }}
                    >
                        <FiX size={14} />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="form-group" style={{ position: "relative" }}>
            <label className="form-label">
                {label}{" "}
                <span style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 400 }}>
                    (optional)
                </span>
            </label>
            <div className="search-bar">
                <FiSearch size={14} color="#9ca3af" />
                <input
                    placeholder={placeholder}
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                    onBlur={() => setTimeout(() => setOpen(false), 180)}
                />
            </div>
            {open && query.length >= 2 && (
                <div
                    style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        background: "#fff",
                        border: "1px solid var(--border)",
                        borderRadius: "var(--radius)",
                        zIndex: 50,
                        boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                        maxHeight: 220,
                        overflowY: "auto",
                    }}
                >
                    {loading && (
                        <div style={{ padding: "0.75rem 1rem", fontSize: 13, color: "var(--text-muted)" }}>
                            Searching…
                        </div>
                    )}
                    {!loading && results.length === 0 && (
                        <div style={{ padding: "0.75rem 1rem", fontSize: 13, color: "var(--text-muted)" }}>
                            No records found.
                        </div>
                    )}
                    {results.map((r) => (
                        <div
                            key={r._id}
                            onMouseDown={() => {
                                onSelect(r);
                                setQuery("");
                                setOpen(false);
                            }}
                            style={{
                                padding: "0.6rem 0.9rem",
                                cursor: "pointer",
                                borderBottom: "1px solid var(--border)",
                                fontSize: 13,
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = "#f0f9ff";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = "transparent";
                            }}
                        >
                            <div style={{ fontWeight: 600 }}>{r.fullName}</div>
                            <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                                {isEmployee
                                    ? `${r.employeeCode} · ${r.department}`
                                    : `${r.enrollmentNo} · ${r.course?.shortName || "Course"} ${r.yearNumber ? `Year ${r.yearNumber}` : ""}`}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function UserForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(id);
    const { user: currentUser, allColleges, selectedCollegeId } = useApp();
    const isSuperAdmin = currentUser?.role === "superadmin";

    const [form, setForm] = useState(() => ({
        ...EMPTY_FORM,
        college: selectedCollegeId && selectedCollegeId !== "all" ? selectedCollegeId : "",
    }));
    const [linkedEmpData, setLinkedEmpData] = useState(null);
    const [linkedStuData, setLinkedStuData] = useState(null);
    const [showPw, setShowPw] = useState(false);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(isEdit);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (!isEdit) return;
        setFetching(true);
        userService
            .getById(id)
            .then((res) => {
                const u = res.data;
                setForm({
                    username: u.username,
                    password: "",
                    displayName: u.displayName || "",
                    role: u.role,
                    linkedEmployee: u.linkedEmployee?._id || "",
                    linkedStudent: u.linkedStudent?._id || "",
                    college: u.college?._id || u.college || "",
                    isActive: u.isActive,
                });
                if (u.linkedEmployee) setLinkedEmpData(u.linkedEmployee);
                if (u.linkedStudent) setLinkedStuData(u.linkedStudent);
            })
            .catch((err) => {
                toast.error(err.message);
                navigate("/users");
            })
            .finally(() => setFetching(false));
    }, [id, isEdit, navigate]);

    const set = (field, val) => {
        setForm((p) => ({ ...p, [field]: val }));
        setErrors((p) => ({ ...p, [field]: "" }));
    };

    const handleRoleChange = (newRole) => {
        setForm((p) => ({
            ...p,
            role: newRole,
            linkedEmployee: "",
            linkedStudent: "",
        }));
        setLinkedEmpData(null);
        setLinkedStuData(null);
    };

    const handleSelectEmployee = (emp) => {
        setLinkedEmpData(emp);
        setForm((p) => ({
            ...p,
            linkedEmployee: emp._id,
            displayName: p.displayName || emp.fullName,
            username: p.username || emp.employeeCode?.toLowerCase().replace(/\s/g, "") || "",
        }));
    };

    const handleSelectStudent = (stu) => {
        setLinkedStuData(stu);
        setForm((p) => ({
            ...p,
            linkedStudent: stu._id,
            displayName: p.displayName || stu.fullName,
            username: p.username || stu.enrollmentNo?.toLowerCase().replace(/\s/g, "") || "",
        }));
    };

    const validate = () => {
        const errs = {};
        if (isSuperAdmin && !form.college) errs.college = "Target college is required.";
        if (!form.username.trim()) errs.username = "Username is required.";
        if (!isEdit && !form.password) errs.password = "Password is required for new accounts.";
        if (form.password && form.password.length < 6)
            errs.password = "Password must be at least 6 characters.";
        if (!form.role) errs.role = "Role is required.";
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setLoading(true);
        try {
            const isStaff = STAFF_ROLES.includes(form.role);
            const isStudentOrParent = ["student", "parent"].includes(form.role);

            const payload = {
                username: form.username.trim().toLowerCase(),
                displayName: form.displayName.trim() || form.username.trim(),
                role: form.role,
                isActive: form.isActive,
                linkedEmployee: isStaff ? form.linkedEmployee || null : null,
                linkedStudent: isStudentOrParent ? form.linkedStudent || null : null,
            };

            if (isSuperAdmin && form.college) payload.college = form.college;
            if (form.password) payload.password = form.password;

            if (isEdit) {
                const res = await userService.update(id, payload);
                toast.success(res.message);
            } else {
                const res = await userService.create(payload);
                toast.success(res.message);
            }
            navigate("/users");
        } catch (err) {
            toast.error(err.message || "Failed to save account.");
        } finally {
            setLoading(false);
        }
    };

    const strength = (() => {
        const pw = form.password;
        if (!pw) return 0;
        let s = 0;
        if (pw.length >= 6) s++;
        if (pw.length >= 10) s++;
        if (/[A-Z]/.test(pw)) s++;
        if (/[0-9]/.test(pw)) s++;
        if (/[^A-Za-z0-9]/.test(pw)) s++;
        return s;
    })();
    const strengthColors = ["", "#dc2626", "#d97706", "#003b67", "#16a34a", "#059669"];
    const strengthLabels = ["", "Weak", "Fair", "Good", "Strong", "Very Strong"];

    const rc = ROLE_CONFIG[form.role] || ROLE_CONFIG.student;
    const isStaffRole = STAFF_ROLES.includes(form.role);

    if (fetching)
        return (
            <div className="loading-center">
                <div className="spinner" />
                Loading account…
            </div>
        );

    return (
        <div>
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
                <div>
                    <h1 className="page-title">{isEdit ? "Edit Account" : "Create Account"}</h1>
                    <p className="page-subtitle">
                        {isEdit ? `Editing ${form.username}` : "Add a new user account"}
                    </p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button className="btn btn-outline" onClick={() => navigate("/users")}>
                        <FiX size={14} /> Cancel
                    </button>
                    <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
                        <FiSave size={14} /> {loading ? "Saving…" : isEdit ? "Update Account" : "Create Account"}
                    </button>
                </div>
            </div>

            <form onSubmit={handleSubmit} style={{ display: "grid", gap: "1rem", maxWidth: 740 }}>
                {/* Role selection */}
                <div className="card" style={{ marginBottom: "1.5rem" }}>
                    <div className="card-header">Select Role</div>
                    <div className="card-body">
                        {isSuperAdmin && (
                            <div className="form-group" style={{ marginBottom: "1.25rem" }}>
                                <label className="form-label" style={{ fontWeight: 700 }}>
                                    Target College Campus <span style={{ color: "var(--danger)" }}>*</span>
                                </label>
                                <select
                                    className={`form-control ${errors.college ? "error" : ""}`}
                                    value={form.college || ""}
                                    onChange={(e) => set("college", e.target.value)}
                                    disabled={isEdit}
                                    style={{ fontSize: 13, fontWeight: 600, color: "#003b67" }}
                                >
                                    <option value="">Select College Campus…</option>
                                    {allColleges.map((c) => (
                                        <option key={c._id} value={c._id}>
                                            {c.name} ({c.code}) — {c.discipline || "General"}
                                        </option>
                                    ))}
                                </select>
                                {errors.college && <span className="form-error">{errors.college}</span>}
                            </div>
                        )}

                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                                gap: "0.65rem",
                            }}
                        >
                            {(isSuperAdmin ? ROLES : ROLES.filter((r) => r !== "admin")).map((r) => {
                                const cfg = ROLE_CONFIG[r];
                                const Icon = cfg.icon;
                                const active = form.role === r;
                                return (
                                    <div
                                        key={r}
                                        onClick={() => !isEdit && handleRoleChange(r)}
                                        style={{
                                            padding: "0.85rem 1rem",
                                            borderRadius: 8,
                                            border: `2px solid ${active ? cfg.color : "var(--border)"}`,
                                            background: active ? cfg.bg : "#fff",
                                            cursor: isEdit ? "not-allowed" : "pointer",
                                            opacity: isEdit && form.role !== r ? 0.45 : 1,
                                            transition: "all 0.15s",
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "0.5rem",
                                                marginBottom: 4,
                                            }}
                                        >
                                            <Icon size={14} color={active ? cfg.color : "#6b7280"} />
                                            <span
                                                style={{
                                                    fontWeight: 700,
                                                    fontSize: 13.5,
                                                    color: active ? cfg.color : "var(--text)",
                                                }}
                                            >
                                                {cfg.label}
                                            </span>
                                        </div>
                                        <div style={{ fontSize: 11.5, color: "var(--text-muted)", lineHeight: 1.4 }}>
                                            {cfg.hint}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {isEdit && (
                            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: "0.6rem" }}>
                                ℹ Role cannot be changed after account creation.
                            </div>
                        )}
                    </div>
                </div>

                {/* Account details */}
                <div className="card">
                    <div className="card-header">Account Details</div>
                    <div className="card-body">
                        <div className="form-grid">
                            {/* Username */}
                            <div className="form-group">
                                <label className="form-label">
                                    Username <span style={{ color: "var(--danger)" }}>*</span>
                                </label>
                                <input
                                    name="username"
                                    className={`form-control ${errors.username ? "error" : ""}`}
                                    value={form.username}
                                    onChange={(e) => set("username", e.target.value.toLowerCase().trim())}
                                    placeholder={
                                        form.role === "student"
                                            ? "e.g. 2023dph001 or ID"
                                            : form.role === "parent"
                                                ? "e.g. parent mobile number"
                                                : isStaffRole
                                                    ? "e.g. employee code or name"
                                                    : "admin username"
                                    }
                                    autoFocus={!isEdit}
                                />
                                {errors.username ? (
                                    <span className="form-error">{errors.username}</span>
                                ) : (
                                    <span className="form-hint">Lowercase only. Used to log in.</span>
                                )}
                            </div>

                            {/* Display name */}
                            <div className="form-group">
                                <label className="form-label">Display Name</label>
                                <input
                                    name="displayName"
                                    className="form-control"
                                    value={form.displayName}
                                    onChange={(e) => set("displayName", e.target.value)}
                                    placeholder="Full name shown in the portal"
                                />
                            </div>

                            {/* Password */}
                            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                                <label className="form-label">
                                    {isEdit ? "New Password" : "Password"}{" "}
                                    {!isEdit && <span style={{ color: "var(--danger)" }}>*</span>}
                                    {isEdit && (
                                        <span style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 400 }}>
                                            (leave blank to keep current)
                                        </span>
                                    )}
                                </label>
                                <div style={{ position: "relative" }}>
                                    <input
                                        type={showPw ? "text" : "password"}
                                        className={`form-control ${errors.password ? "error" : ""}`}
                                        style={{ paddingRight: 40 }}
                                        value={form.password}
                                        onChange={(e) => set("password", e.target.value)}
                                        placeholder="Min. 6 characters"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPw((p) => !p)}
                                        style={{
                                            position: "absolute",
                                            right: 10,
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            background: "none",
                                            border: "none",
                                            cursor: "pointer",
                                            color: "var(--text-muted)",
                                            padding: 4,
                                        }}
                                    >
                                        {showPw ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                                    </button>
                                </div>
                                {errors.password && <span className="form-error">{errors.password}</span>}
                                {form.password && (
                                    <div style={{ marginTop: 6 }}>
                                        <div
                                            style={{
                                                height: 4,
                                                borderRadius: 4,
                                                background: "var(--border)",
                                                overflow: "hidden",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    height: "100%",
                                                    width: `${(strength / 5) * 100}%`,
                                                    background: strengthColors[strength],
                                                    borderRadius: 4,
                                                    transition: "width 0.3s",
                                                }}
                                            />
                                        </div>
                                        <span
                                            style={{
                                                fontSize: 11.5,
                                                color: strengthColors[strength],
                                                fontWeight: 600,
                                            }}
                                        >
                                            {strengthLabels[strength]}
                                        </span>
                                    </div>
                                )}
                                {(form.role === "student" || form.role === "parent") && !isEdit && (
                                    <span className="form-hint">
                                        Student/Parent must change this password on their first login.
                                    </span>
                                )}
                            </div>

                            {/* Active toggle */}
                            <label
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.4rem",
                                    fontSize: 13,
                                    cursor: "pointer",
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={form.isActive}
                                    onChange={(e) => set("isActive", e.target.checked)}
                                />
                                Account is active (user can log in)
                            </label>
                        </div>
                    </div>
                </div>

                {/* Linked record for staff roles */}
                {isStaffRole && (
                    <div className="card">
                        <div
                            className="card-header"
                            style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
                        >
                            <FiBriefcase size={13} color={rc.color} /> Link to Employee Record
                        </div>
                        <div className="card-body">
                            <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: "0.85rem" }}>
                                Link this {rc.label.toLowerCase()} account to their employee profile so their
                                details, payslips, and attendance are linked.
                            </p>
                            <LinkedRecordPicker
                                type="employee"
                                value={form.linkedEmployee}
                                displayValue={
                                    linkedEmpData
                                        ? `${linkedEmpData.fullName} — ${linkedEmpData.employeeCode || ""}`
                                        : ""
                                }
                                onSelect={handleSelectEmployee}
                                onClear={() => {
                                    setLinkedEmpData(null);
                                    set("linkedEmployee", "");
                                }}
                            />
                        </div>
                    </div>
                )}

                {/* Linked record for student/parent */}
                {(form.role === "student" || form.role === "parent") && (
                    <div className="card">
                        <div
                            className="card-header"
                            style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
                        >
                            <FiUser size={13} color="#003b67" />
                            {form.role === "parent"
                                ? "Link to Child's Student Record"
                                : "Link to Student Record"}
                        </div>
                        <div className="card-body">
                            <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: "0.85rem" }}>
                                {form.role === "parent"
                                    ? "Link this parent account to their child's student record to view attendance, results, and fees."
                                    : "Link this account to the student's record to load academic profile data."}
                            </p>
                            <LinkedRecordPicker
                                type="student"
                                value={form.linkedStudent}
                                displayValue={
                                    linkedStuData
                                        ? `${linkedStuData.fullName} — ${linkedStuData.enrollmentNo || ""}`
                                        : ""
                                }
                                onSelect={handleSelectStudent}
                                onClear={() => {
                                    setLinkedStuData(null);
                                    set("linkedStudent", "");
                                }}
                            />
                        </div>
                    </div>
                )}

                {/* Submit Bar */}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                    <button
                        type="button"
                        className="btn btn-outline"
                        onClick={() => navigate("/users")}
                    >
                        <FiX size={14} /> Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                        <FiSave size={14} />{" "}
                        {loading ? "Saving…" : isEdit ? "Update Account" : "Create Account"}
                    </button>
                </div>
            </form>
        </div>
    );
}